'use client';
import type { ProductDisplayData as Product } from '@eliadmin/core/src/api/products/types/product-types';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';
import { useEffect, useRef, useState } from 'react';
import { MdOutlineShoppingBag } from 'react-icons/md';

export type CardCartProps = {
  product: Product;
  sellable: boolean;
  showIcon: boolean;
  className?: string;
};
type Runtime = typeof import('./cart-button-runtime').CardCartRuntime;
let runtimePromise: Promise<Runtime> | undefined;
function loadRuntime() {
  return (runtimePromise ??= import('./cart-button-runtime')
    .then(m => m.CardCartRuntime)
    .catch(error => {
      runtimePromise = undefined;
      throw error;
    }));
}
export function DeferredCardCartButton(props: CardCartProps) {
  const { t } = useTranslations();
  const initialized = useCustomerStore(s => s.isInitialized);
  const [Runtime, setRuntime] = useState<Runtime>();
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const host = useRef<HTMLSpanElement>(null);
  const mounted = useRef(false);
  const inFlight = useRef(false);
  const pendingClick = useRef(false);
  const restoreFocus = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  function activate(click = false) {
    if (!click) {
      void loadRuntime().catch(() => {});
      return;
    }
    pendingClick.current = true;
    restoreFocus.current = !!host.current?.contains(document.activeElement);
    if (inFlight.current) return;
    inFlight.current = true;
    setLoading(true);
    setFailed(false);
    loadRuntime()
      .then(component => {
        if (mounted.current) setRuntime(() => component);
      })
      .catch(() => {
        pendingClick.current = false;
        if (mounted.current) setFailed(true);
      })
      .finally(() => {
        inFlight.current = false;
        if (mounted.current) setLoading(false);
      });
  }
  useEffect(() => {
    if (!Runtime) return;
    const button = host.current?.querySelector<HTMLButtonElement>('button');
    const click = pendingClick.current;
    pendingClick.current = false;
    if (restoreFocus.current) button?.focus({ preventScroll: true });
    if (click && button && !button.disabled) button.click();
  }, [Runtime]);
  const supplier = [...(props.product.suppliers ?? [])].sort((a, b) => a.position - b.position)[0];
  const icon = props.showIcon ? (
    <MdOutlineShoppingBag aria-hidden="true" className="h-5 w-5" />
  ) : null;
  if (supplier?.is_affiliate)
    return (
      <Button asChild className={props.className}>
        <a href={supplier.url ?? '#'}>
          {icon}
          {t('text1115')}
        </a>
      </Button>
    );
  const unresolvedVariants = !!props.product.has_variants || !!props.product.variants?.length;
  if (!unresolvedVariants && getProductPriceDisplay(props.product).priceOnRequest) return null;
  return (
    <span ref={host} className="contents">
      {Runtime ? (
        <Runtime {...props} />
      ) : (
        <Button
          type="button"
          className={props.className}
          disabled={!initialized || !props.sellable || props.product.status === 'ended'}
          aria-busy={loading}
          onPointerEnter={() => activate()}
          onFocus={() => activate()}
          onPointerDown={() => activate()}
          onClick={event => {
            event.preventDefault();
            event.stopPropagation();
            activate(true);
          }}
        >
          {icon}
          <span className="line-clamp-1 flex text-ellipsis @[200px]:hidden">{t('text1115')}</span>
          <span className="line-clamp-1 hidden text-ellipsis @[200px]:flex">{t('text1115')}</span>
        </Button>
      )}
      {failed && (
        <span role="alert" className="sr-only">
          {t('text657')}
        </span>
      )}
    </span>
  );
}
